import { getLanguageLabelByCode } from '../../constants/videoLanguages'

const isLikelyLanguageCode = value =>
  typeof value === 'string' && /^[a-z]{2,3}(-[a-z]{2})?$/i.test(value.trim())

const toTitleCase = value =>
  typeof value === 'string' && value.trim()
    ? value
        .trim()
        .split(/\s+/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
    : ''

const isOpaqueVoiceIdentifier = value => {
  if (typeof value !== 'string') return false
  const normalized = value.trim()
  return (
    normalized.length >= 12 &&
    /^[a-z0-9]+$/i.test(normalized) &&
    /[a-z]/i.test(normalized) &&
    /\d/.test(normalized) &&
    !normalized.includes('_') &&
    !normalized.includes('-')
  )
}

const getNormalizedVoiceName = (voice, id, fallbackType, index) => {
  const explicitName =
    voice.name ||
    voice.voice_name ||
    voice.voice_id ||
    voice.display_name ||
    voice.label

  if (explicitName) {
    return explicitName
  }

  if (typeof id === 'string' && !isOpaqueVoiceIdentifier(id)) {
    return id
  }

  return `${
    fallbackType === 'uploaded' ? 'Custom Voice' : 'System Voice'
  } ${index + 1}`
}

export const normalizeVoiceLanguages = languages => {
  if (!languages) return []

  const normalizedLanguages = Array.isArray(languages) ? languages : [languages]

  return normalizedLanguages
    .map(language => {
      if (!language) return null

      if (typeof language === 'string') {
        const normalized = language.trim()
        if (!normalized) return null
        return isLikelyLanguageCode(normalized)
          ? getLanguageLabelByCode(normalized.toLowerCase())
          : toTitleCase(normalized)
      }

      const code =
        language.code ||
        language.language_code ||
        language.lang ||
        language.value
      const name =
        language.name ||
        language.language ||
        language.label ||
        language.display_name

      if (name) return toTitleCase(name)
      if (code) return getLanguageLabelByCode(String(code).toLowerCase())
      return null
    })
    .filter(Boolean)
    .filter((language, index, allLanguages) => {
      return allLanguages.indexOf(language) === index
    })
}

export const normalizeVoiceEntry = (
  voice,
  fallbackType = 'default',
  index = 0
) => {
  if (!voice) return null

  if (typeof voice === 'string') {
    return {
      id: voice,
      name: voice,
      type: fallbackType,
      description: '',
      languages: []
    }
  }

  const id =
    voice.id ||
    voice.voice_id ||
    voice.voiceId ||
    voice.name ||
    voice.voice_name

  if (!id) return null

  return {
    ...voice,
    id,
    name: getNormalizedVoiceName(voice, id, fallbackType, index),
    type: voice.type || fallbackType,
    description:
      voice.description || voice.voice_description || voice.prompt || '',
    languages: normalizeVoiceLanguages(
      voice.languages || voice.language || voice.verified_languages
    )
  }
}

export const normalizeVoiceList = (voices, fallbackType = 'default') =>
  (Array.isArray(voices) ? voices : [])
    .map((voice, index) => normalizeVoiceEntry(voice, fallbackType, index))
    .filter(Boolean)

export const mergeVoicesById = (...voiceCollections) => {
  const merged = new Map()

  voiceCollections.flat().forEach(voice => {
    if (!voice?.id) return
    const previous = merged.get(voice.id) || {}
    merged.set(voice.id, {
      ...previous,
      ...voice,
      languages: normalizeVoiceLanguages([
        ...(previous.languages || []),
        ...(voice.languages || [])
      ])
    })
  })

  return Array.from(merged.values())
}

const extractVoiceNameFromDuplicateError = rawMessage => {
  if (typeof rawMessage !== 'string') return ''
  const match = rawMessage.match(
    /voice\s+['"]?([^'"]+?)['"]?\s+already exists/i
  )
  return match?.[1]?.trim() || ''
}

const getRawVoiceErrorMessage = error => {
  const detail = error?.response?.data?.detail
  const apiError = error?.response?.data?.error

  return (
    (typeof detail === 'string' && detail) ||
    (typeof apiError === 'string' && apiError) ||
    (typeof error?.message === 'string' && error.message) ||
    ''
  )
}

export const formatVoiceUploadError = (error, voiceName = '') => {
  const status = error?.response?.status
  const rawMessage = getRawVoiceErrorMessage(error)
  const normalizedMessage = rawMessage.toLowerCase()
  const networkLikeError =
    normalizedMessage.includes('network error') ||
    normalizedMessage.includes('request entity too large') ||
    normalizedMessage.includes('content too large') ||
    normalizedMessage.includes('payload too large')

  if (status === 413) {
    return 'File is too large. Please upload a file smaller than 50MB.'
  }

  if (networkLikeError) {
    return 'Upload failed. If your file is too large, please keep it at or below 50MB and try again.'
  }

  if (normalizedMessage.includes('already exists')) {
    const resolvedVoiceName =
      voiceName?.trim() || extractVoiceNameFromDuplicateError(rawMessage)

    if (resolvedVoiceName) {
      return `A voice named "${resolvedVoiceName}" already exists. Please rename your voice or choose to overwrite the existing one.`
    }

    return 'A voice with this name already exists. Please rename your voice or choose to overwrite the existing one.'
  }

  if (
    normalizedMessage.includes('task processing failed') ||
    normalizedMessage.includes('missing task id')
  ) {
    return 'We could not finish processing your voice upload. Please try again in a moment.'
  }

  if (rawMessage) {
    return rawMessage
  }

  return 'Failed to upload voice'
}

export const formatVoiceSaveError = (error, voiceName = '') => {
  const rawMessage = getRawVoiceErrorMessage(error)
  const normalizedMessage = rawMessage.toLowerCase()

  if (
    normalizedMessage.includes('already exists') ||
    normalizedMessage.includes('overwrite')
  ) {
    const resolvedVoiceName =
      voiceName?.trim() || extractVoiceNameFromDuplicateError(rawMessage)

    if (resolvedVoiceName) {
      return `A voice named "${resolvedVoiceName}" already exists. Rename this voice and save again.`
    }

    return 'A voice with this name already exists. Rename this voice and save again.'
  }

  if (
    normalizedMessage.includes('task processing failed') ||
    normalizedMessage.includes('missing task id')
  ) {
    return 'We could not finish saving this voice. Please try again in a moment.'
  }

  if (rawMessage) {
    return rawMessage
  }

  return 'Failed to save voice'
}

export const uploadSynthesizedVoiceFromUrl = async ({
  audioUrl,
  voiceName,
  language,
  description,
  getToken,
  uploadVoiceFromUrl,
  voiceListPoller,
  fetchVoices
}) => {
  if (!audioUrl) {
    throw new Error('No synthesized audio URL was returned')
  }

  const resolvedVoiceName = voiceName?.trim() || `voice_${Date.now()}`
  const uploadFormData = new FormData()
  uploadFormData.append('audio_url', audioUrl)
  uploadFormData.append('voice_name', resolvedVoiceName)
  uploadFormData.append(
    'language',
    String(language || 'english').toLowerCase()
  )

  if (description) {
    uploadFormData.append('description', description)
  }

  const token = await getToken()
  const uploadResponse = await uploadVoiceFromUrl(uploadFormData, token)

  const uploadResult = await voiceListPoller.resolveTaskData(uploadResponse, {
    getDirectOutput: data =>
      data && data.output !== undefined ? data.output : data,
    isDirectResponse: (data, output) => {
      const status =
        typeof data?.status === 'string' ? data.status.toLowerCase() : ''
      return (
        status === 'success' ||
        status === 'completed' ||
        status === 'done' ||
        data?.success === true ||
        !!(output && (output.voice_id || output.id)) ||
        !!data?.voice_id
      )
    }
  })

  if (typeof fetchVoices === 'function') {
    await fetchVoices()
  }

  return uploadResult
}
